import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, formatApiError, API } from "@/lib/api";
import { Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, ServerCog } from "lucide-react";
import { toast } from "sonner";

const q = (s) => `'${s.replace(/'/g, "'\\''")}'`;

export const GATEWAY_FOLDER = "/opt/tradify-gateway";

const SHA = /^[0-9a-f]{64}$/;

export function buildCommand(sha, readerSha, manifest) {
  if (!SHA.test(sha) || !SHA.test(readerSha)) return "";
  const exp = manifest ? ` --expected ${q(manifest)}` : "";
  const verify = `import hashlib,sys;s=sys.stdin.buffer.read();hashlib.sha256(s).hexdigest()=="${readerSha}" or sys.exit("reader SHA-256 mismatch");exec(compile(s,"tradify_reader","exec"),{"__name__":"__main__"})`;
  return ` ( set -o pipefail; F=${q(`${API}/gateway-transfer`)}; curl -fsSL "$F/exporter.py" -o ~/tradify_exporter.py && curl -fsSL "$F/reader.py" -o ~/tradify_reader.py && printf '%s *%s\\n' ${sha} "$HOME/tradify_exporter.py" ${readerSha} "$HOME/tradify_reader.py" | sha256sum --strict -c - && sudo -n /usr/bin/python3 -I -S -c '${verify}' < ~/tradify_reader.py | python3 -I ~/tradify_exporter.py ${GATEWAY_FOLDER}${exp} --extra-tar - --upload "$F/upload" ); echo "RESULT exit=$?"`;
}

async function sha256Hex(s) {
  const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function GatewayTransferPanel() {
  const [pw, setPw] = useState("");
  const [cmdSha, setCmdSha] = useState("");
  const [manifest, setManifest] = useState("");
  const [issued, setIssued] = useState(null);
  const revoke = useMutation({
    mutationFn: async () => (await api.post("/gateway-transfer/codes/revoke")).data,
    onSuccess: (r) => { setIssued(null); toast.success(`Revoked ${r.revoked} unused code(s)`); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const imports = useQuery({ queryKey: ["gateway-imports"], refetchInterval: issued ? 8000 : false,
    queryFn: async () => (await api.get("/gateway-transfer/imports")).data });
  const create = useMutation({
    mutationFn: async () => {
      const isCode = /^\d{6}$/.test(pw);
      const { data } = await api.post("/auth/step-up/gateway-transfer", isCode ? { totp_code: pw } : { password: pw });
      return (await api.post("/gateway-transfer/codes", { step_up_token: data.step_up_token })).data;
    },
    onSuccess: (r) => { setIssued(r); setPw(""); toast.success("One-time code created (30 minutes)"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const cmd = buildCommand(imports.data?.exporter_sha256 || "", imports.data?.reader_sha256 || "", manifest.trim());
  useEffect(() => { if (cmd) { sha256Hex(cmd).then(setCmdSha); } }, [cmd]);
  return (
    <Panel title="Sharekhan Gateway · one-time code transfer" testId="gateway-transfer-panel" actions={<ServerCog className="w-4 h-4 text-slate-500" />}>
      <div className="p-4 space-y-3 text-xs">
        <p className="text-slate-400">Copies the gateway's source code here for review, without secrets. The command only reads files on the gateway, checks the exporter's SHA-256 before running it, and sends one zip back with a single-use code. Nothing on the gateway changes.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div data-testid="gateway-folder-fixed" className="rounded border border-[var(--border-subtle)] px-3 py-2 font-mono text-slate-300">Gateway folder (fixed): {GATEWAY_FOLDER}</div>
          <Input data-testid="gateway-manifest-input" value={manifest} onChange={(e) => setManifest(e.target.value)} placeholder="SHA-256 manifest file (optional)" className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] font-mono" />
        </div>
        {!issued && (
          <div className="flex gap-2">
            <Input data-testid="gateway-reverify-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password or 6-digit code" className="bg-[var(--bg-canvas)] border-[var(--border-subtle)]" />
            <Button data-testid="gateway-create-code-btn" onClick={() => create.mutate()} disabled={!pw || create.isPending} className="bg-blue-600 hover:bg-blue-500 text-white shrink-0">Create one-time code</Button>
          </div>
        )}
        {issued && (
          <div className="space-y-2" data-testid="gateway-command-block">
            <div className="text-amber-300">Code expires {issued.expires_at.slice(11, 16)} UTC · single use · hidden on screen. Paste it only when the gateway asks for it.</div>
            <div className="flex gap-2">
              <Button size="sm" data-testid="gateway-copy-code-btn" onClick={() => { navigator.clipboard.writeText(issued.code); toast.success("Code copied (not shown)"); }} className="bg-blue-600 hover:bg-blue-500 text-white"><Copy className="w-3.5 h-3.5 mr-1" />Copy hidden code</Button>
              <Button size="sm" variant="outline" data-testid="gateway-revoke-btn" onClick={() => revoke.mutate()} className="border-[var(--border-subtle)]">Revoke code</Button>
            </div>
          </div>
        )}
        {cmd && (
          <div className="space-y-1">
            <div className="text-slate-400">Command (contains no code; safe to show) · SHA-256 <span data-testid="gateway-command-sha" className="font-mono text-slate-300 break-all">{cmdSha}</span></div>
            <div className="flex gap-2 items-start">
              <code data-testid="gateway-command" className="flex-1 break-all whitespace-pre-wrap rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] p-2 font-mono text-[11px] text-slate-200">{cmd}</code>
              <Button size="sm" variant="outline" data-testid="gateway-copy-btn" onClick={() => { navigator.clipboard.writeText(cmd); toast.success("Command copied"); }} className="border-[var(--border-subtle)]"><Copy className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        )}
        <div data-testid="gateway-imports-list" className="text-slate-400">Received bundles: {imports.data?.items?.length ? imports.data.items.map((i) => (
          <span key={i.id} className={i.status === "rejected" ? "text-rose-400 mr-2" : "text-emerald-400 mr-2"}>{i.bundle || "rejected"} ({i.file_count ?? i.error})</span>)) : "none yet"}</div>
      </div>
    </Panel>
  );
}
